/**
 * OfferListView — flat list mode for the Offers page.
 *
 * Rendered when the user toggles to "List" view (or when there's only one
 * pending offer and compare mode is unavailable). One `OfferListRow` per
 * offer; the row shows lender, headline pricing (amount / rate / monthly),
 * deadline chip, and a "View" affordance that navigates to the existing
 * ProposalDetailPage.
 */

import ProposalIcon from '@mui/icons-material/Description';
import { Box, EmptyState, GlassCard, useI18n } from '@ops-matrix/design-system-ui';
import { OfferListRow } from './OfferListRow';
import type { OfferRecord } from './types';

export interface OfferListViewProps {
  offers: OfferRecord[];
  /** Navigate to the detail page (kept abstract so we don't import react-router here). */
  onView: (offer: OfferRecord) => void;
  onAccept: (offer: OfferRecord) => void;
  onCounter: (offer: OfferRecord) => void;
  onAsk: (offer: OfferRecord) => void;
}

export function OfferListView({ offers, onView, onAccept, onCounter, onAsk }: OfferListViewProps) {
  const { t } = useI18n();

  if (offers.length === 0) {
    return (
      <EmptyState
        icon={<ProposalIcon />}
        title={t('consumer.offers.noOffers')}
        description={t('consumer.offers.noOffersDescription')}
      />
    );
  }

  return (
    <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {offers.map((offer, idx) => (
          <OfferListRow
            key={offer.id}
            offer={offer}
            isLast={idx === offers.length - 1}
            onView={onView}
            onAccept={onAccept}
            onCounter={onCounter}
            onAsk={onAsk}
          />
        ))}
      </Box>
    </GlassCard>
  );
}

OfferListView.displayName = 'OfferListView';
